import { TableCell, TableRow } from '@material-ui/core'
import React, { EventHandler, KeyboardEvent, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ReactComponent as DropdownSVG } from '@percent/admin-dashboard/common/assets/images/dropdown.svg'
import { ErrorView, SearchAndDateFilter, Table, TimeLeft } from '@percent/admin-dashboard/common/components'
import { CountryCodeFlag } from '@percent/admin-dashboard/common/components/countryCodeFlag/CountryCodeFlag'
import { TableSort } from '@percent/admin-dashboard/common/components/table/Table.types'
import { useGetQuery } from '@percent/admin-dashboard/common/hooks/useGetQuery/useGetQuery'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { useBadgeTotal } from '@percent/admin-dashboard/context/badgeTotal/useBadgeTotal/useBadgeTotal'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import styles from './ActivityCheckTable.module.scss'
import { ActivityCheckStatusFilter, ActivityCheckTableProps } from './ActivityCheckTable.types'

export function ActivityCheckTable({
  activities,
  isLoading,
  totalResults,
  nextPage,
  previousPage,
  errorMessage,
  queryParams,
  setQueryParams
}: ActivityCheckTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { totalNumber, setTotalNumber } = useBadgeTotal()
  const query = useGetQuery()
  const getQueryStatus = query.get('status')

  useEffect(() => {
    if (getQueryStatus === 'pending') {
      setTotalNumber({
        ...totalNumber,
        totalPendingActivity: totalResults ?? 0
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalResults])

  const tabs = useMemo(() => [t('table.pending'), t('table.cancelled'), t('table.live')], [t])

  const cells = useMemo(() => {
    const commonCells = [
      { id: 'country', isSortable: false },
      {
        id: 'organization',
        isSortable: false
      },
      { id: 'code', isSortable: false },
      { id: 'partner', isSortable: false },
      { id: 'requestDate', isSortable: true }
    ]

    if (queryParams.status === 'pending') {
      commonCells.push({ id: 'timeLeft', isSortable: true })
    }

    return commonCells
  }, [queryParams.status])

  const handleRequestSort = () =>
    setQueryParams({ direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC', sort: 'createdAt' })

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    const status = ActivityCheckStatusFilter[newValue]

    if (status === 'pending') {
      setQueryParams({ status, sort: 'createdAt', direction: 'ASC' })
    } else {
      setQueryParams({ status, sort: undefined, direction: undefined })
    }
  }

  const handleRowClick = (id: string) => {
    if (queryParams.status === 'pending' || queryParams.status === 'live') {
      return handleClickAsLink(`/activity/${id}`, navigate)
    }

    return undefined
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value
        })
      }
    },
    [setQueryParams]
  )

  const filters = useMemo(
    () => (
      <SearchAndDateFilter
        handleKeyPress={handleKeyPress}
        searchPhrase={queryParams.query ?? ''}
        placeholder={t('table.adminACSearchOrganisationPlaceholder')}
        setQueryParams={setQueryParams}
      />
    ),
    [handleKeyPress, queryParams.query, t, setQueryParams]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Table
      isLoading={isLoading}
      totalResults={totalResults}
      data={activities}
      value={
        ActivityCheckStatusFilter[queryParams.status as keyof typeof ActivityCheckStatusFilter] ??
        ActivityCheckStatusFilter.pending
      }
      handleChange={handleTabChange}
      nextPage={nextPage}
      previousPage={previousPage}
      columns={cells}
      tabs={tabs}
      order={(queryParams.direction?.toLowerCase() as TableSort) ?? 'asc'}
      orderBy="requestDate"
      handleRequestSort={handleRequestSort}
      emptyTableText={t('table.emptyActivitiesChecks')}
      filtersContent={filters}
    >
      {activities?.map(activity => (
        <TableRow
          key={activity.id}
          className={activity.organisation?.id ? styles.row : undefined}
          onClick={activity.organisation?.id ? handleRowClick(activity.id) : undefined}
        >
          <TableCell>
            <CountryCodeFlag countryCode={activity.countryCode} />
          </TableCell>
          <TableCell>{activity.organisation?.name} </TableCell>
          <TableCell>{activity.organisationActivity?.activity ?? activity.activity}</TableCell>
          <TableCell>{activity.partnerName}</TableCell>
          <TableCell>{getDate(activity.createdAt)}</TableCell>
          {queryParams.status === 'pending' && (
            <TableCell>
              <TimeLeft requestedDate={activity.createdAt} />
            </TableCell>
          )}
          {(queryParams.status === 'pending' || queryParams.status === 'live') && (
            <TableCell align="right" className={styles.rowIconForward}>
              <DropdownSVG className={styles.arrow} />
            </TableCell>
          )}
        </TableRow>
      ))}
    </Table>
  )
}
